import React, {useEffect,useRef,useState} from "react";
import {writeUrlBackground} from "libs/bgNumbering";
import {useSearchParams} from "react-router-dom";
import {useImages} from "contexts/images";
import {useSetting} from "contexts/setting";
import {useBgTransformContext} from "contexts/bgTransform";
import {useSceneScope} from "hooks/useSceneScope";
import "./index.css";

export default function PointCloudBackgrounds() {
  const [params,setParams]=useSearchParams();
  const images:any=useImages();
  const setting:any=useSetting();
  const transforms=useBgTransformContext();
  const scope=useSceneScope();
  const latest=useRef({params,images}); latest.current={params,images};
  const [deleting,setDeleting]=useState("");
  const [enabled,setEnabled]=useState(false), [items,setItems]=useState<any[]>([]), [error,setError]=useState("");
  useEffect(() => {
    if (setting.product?.value === "ALL") return;
    let stopped=false;
    fetch("/api/point-cloud/config").then(async (response) => {
      if (!response.ok) return;
      const config = await response.json();
      if (!config.enabled || (!config.localPrototype && !config.availableToCurrentAccount)) return;
      if (stopped) return; setEnabled(true);
      const result=await fetch("/api/point-cloud/backgrounds",{credentials:"same-origin",cache:"no-store"});
      if (result.status === 403) { if (!stopped) setEnabled(false); return; }
      const value=await result.json(); if (!result.ok) throw new Error(value.error || "Could not load your backgrounds");
      if (!Array.isArray(value.backgrounds)) throw new Error("Invalid personal backgrounds response");
      if (!stopped) setItems(value.backgrounds);
    }).catch((cause) => { if (!stopped) setError(cause.message); });
    return () => { stopped=true; };
  },[setting.product?.value]);
  function select(item: {id:string; url:string}) {
    if (scope.isSelection && scope.applyBackgroundToSelection(item.url)) return;
    if (transforms.enabled) {
      // Keep each own image's current crop when the shared option changes.
      const ownIds=new Set<string>([...Object.keys(setting.templateModes), ...setting.locked]);
      for (const templateId of ownIds) {
        if (scope.modeOf(templateId,"image") !== "own" && !setting.locked.includes(templateId)) continue;
        const transform=transforms.getEffectiveTransform(templateId,setting.bgOption);
        if (transform) images.setUserBgAdjustment("custom",templateId,transform);
      }
    }
    setParams((current) => {
      writeUrlBackground(current,"custom",null); current.set("privateBackground",item.id);
      return current;
    },{replace:true});
  }
  async function remove(item: {id:string;url:string}) {
    if (deleting) return;
    setDeleting(item.id); setError("");
    try {
      const response=await fetch(`/api/point-cloud/backgrounds/${item.id}`,{method:"DELETE",credentials:"same-origin",signal:AbortSignal.timeout(30_000)});
      const result=await response.json(); if (!response.ok) throw new Error(result.error || "Could not delete your background");
      const current=latest.current;
      const scalarMatches=current.images.customBg === item.url;
      if (scalarMatches) current.images.editCustomBg("");
      for (const [templateId,url] of Object.entries(current.images.customBgs)) {
        if (url === item.url) current.images.editCustomBgs(templateId,undefined,true);
      }
      // Keep the background mode: switching to a preset would reset every template transform,
      // including detached Ads backgrounds that use a different image.
      setParams((value) => {
        if (value.get("privateBackground") === item.id) value.delete("privateBackground");
        return value;
      },{replace:true});
      setItems((currentItems) => currentItems.filter((entry) => entry.id !== item.id));
    } catch(cause: any) { setError(cause.name === "TimeoutError" ? "Delete timed out. You can safely retry." : cause.message); }
    finally { setDeleting(""); }
  }
  if (setting.product?.value === "ALL" || !enabled || !items.length) return null;
  return <section className="point-cloud-backgrounds bg-section" aria-label="My Point Cloud backgrounds">
    <h3 className="point-cloud-backgrounds__title">Imported from Point Cloud</h3>
    {error && <p className="point-cloud-backgrounds__message point-cloud-backgrounds__message--error" role="alert">{error}</p>}
    <div className="point-cloud-backgrounds__grid">{items.map((item) => <div className="point-cloud-backgrounds__tile" key={item.id} data-background-id={item.id}>
      <button type="button" className="point-cloud-backgrounds__select" disabled={Boolean(deleting)} title={item.filename} aria-label={`Use ${item.filename}`} aria-pressed={scope.isSelection
        ? scope.selection.every((templateId: string) => images.customBgs[templateId] === item.url)
        : params.get("background") === "custom" && params.get("privateBackground") === item.id} onClick={() => select(item)}>
        <img src={item.thumbnailUrl} alt="" loading="lazy" draggable={false} />
      </button>
      <button type="button" className="point-cloud-backgrounds__delete" disabled={Boolean(deleting)} aria-label={`Delete ${item.filename}`} title={`Delete ${item.filename}`} aria-busy={deleting === item.id} onClick={() => remove(item)}>
        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" focusable="false">
          <path d="M3 6h18M9 6V3h6v3M6 6l1 15h10l1-15M10 10v7M14 10v7" />
        </svg>
      </button>
    </div>)}</div>
  </section>;

}
